// RecommendationCard — displays a prioritized action from recommendations-ranked.md.
// Shows rank badge, impact/effort/cost tags, expected outcome, and evidence link.

export interface RecommendationCardProps {
  rank: number;
  title: string;
  impact: "high" | "medium" | "low" | "very-high";
  effort: "low" | "medium" | "high";
  cost: number;
  expectedOutcome: string;
  evidence: string;
  source: string;
}

const IMPACT_COLORS: Record<RecommendationCardProps["impact"], string> = {
  "very-high":
    "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30",
  high: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  medium:
    "bg-blue-500/15 text-blue-700 dark:text-blue-400 border border-blue-500/30",
  low: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400 border border-zinc-500/20",
};

const IMPACT_LABELS: Record<RecommendationCardProps["impact"], string> = {
  "very-high": "VERY HIGH",
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",
};

const EFFORT_COLORS: Record<RecommendationCardProps["effort"], string> = {
  low: "bg-green-500/15 text-green-700 dark:text-green-400 border border-green-500/30",
  medium:
    "bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30",
  high: "bg-red-500/15 text-red-700 dark:text-red-400 border border-red-500/30",
};

export default function RecommendationCard({
  rank,
  title,
  impact,
  effort,
  cost,
  expectedOutcome,
  evidence,
  source,
}: RecommendationCardProps) {
  const costDisplay = cost === 0 ? "$0" : `$${cost.toLocaleString()}`;

  return (
    <div className="glass rounded-2xl p-4 flex flex-col gap-3 relative overflow-hidden">
      {/* Rank accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{
          backgroundColor:
            rank === 1
              ? "#DC2626"
              : rank === 2
                ? "#D97706"
                : rank <= 4
                  ? "#65A30D"
                  : "#6B7280",
        }}
      />

      {/* Header: rank + title */}
      <div className="flex items-start gap-3 pt-1">
        <span className="shrink-0 w-7 h-7 rounded-full bg-theme-overlay border border-theme-subtle flex items-center justify-center text-xs font-black text-theme-secondary">
          {rank}
        </span>
        <p className="text-sm font-semibold text-theme-primary leading-snug">
          {title}
        </p>
      </div>

      {/* Tags row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${IMPACT_COLORS[impact]}`}
        >
          IMPACT: {IMPACT_LABELS[impact]}
        </span>
        <span
          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${EFFORT_COLORS[effort]}`}
        >
          EFFORT: {effort.toUpperCase()}
        </span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-theme-overlay border border-theme-subtle text-theme-muted">
          COST: {costDisplay}
        </span>
      </div>

      {/* Expected outcome */}
      <p className="text-xs text-theme-secondary leading-snug">
        <span className="font-semibold text-theme-primary">Expected: </span>
        {expectedOutcome}
      </p>

      {/* Evidence + source */}
      <div className="space-y-0.5">
        <p className="text-[10px] text-theme-muted leading-snug">
          <span className="font-medium">Evidence: </span>
          {evidence}
        </p>
        <p className="text-[10px] text-theme-muted">
          <span className="font-medium">Source: </span>
          {source}
        </p>
      </div>
    </div>
  );
}
